import { __assign, __spreadArrays } from "tslib";
import { isDate, isValidDate, Platform } from '@tyler-components-web/core';
import { CalendarDateSelectType, CalendarView } from '../calendar';
import { DateRange } from '../calendar/core/date-range';
import { formatDate, parseDateString } from '../core/date-utils';
import { DEFAULT_DATE_MASK } from '../core/mask/date-input-mask';
import { DatePickerRange, DATE_RANGE_PICKER_CONSTANTS } from './date-range-picker-constants';
var DateRangePickerFoundation = /** @class */ (function () {
    function DateRangePickerFoundation(_adapter) {
        var _this = this;
        this._adapter = _adapter;
        this._from = null;
        this._to = null;
        this._min = null;
        this._max = null;
        this._blockedDates = null;
        this._open = false;
        this._disabled = false;
        this._masked = false;
        this._maskFormat = DEFAULT_DATE_MASK;
        this._showMaskFormat = false;
        this._useCharMask = false;
        this._valueMode = 'object';
        this._notifyInputValueChanges = true;
        this._allowInvalidDate = false;
        this._isInitialized = false;
        this._showToday = false;
        this._showClear = false;
        this._startView = CalendarView.Date;
        this._fromInputListener = function (evt) { return _this._onFromInput(evt); };
        // Using a promise for IE issue which caused the popup to close after opening due to input bluring
        this._fromInputBlurListener = function (evt) { return Promise.resolve().then(function () { return _this._onFromInputBlur(evt); }); };
        this._fromInputValueChangedListener = function (value) { return _this._onFromInputValueChanged(value); };
        this._fromInputKeydownListener = function (evt) { return _this._onInputKeydown(evt); };
        this._fromInputFocusListener = function (evt) { return _this._onFromInputFocus(); };
        this._toInputListener = function (evt) { return _this._onToInput(evt); };
        this._toInputBlurListener = function (evt) { return Promise.resolve().then(function () { return _this._onToInputBlur(evt); }); };
        this._toInputValueChangedListener = function (value) { return _this._onToInputValueChanged(value); };
        this._toInputKeydownListener = function (evt) { return _this._onInputKeydown(evt); };
        this._toInputFocusListener = function (evt) { return _this._onToInputFocus(); };
        this._toggleMousedownListener = function (evt) { return _this._onToggleMousedown(evt); };
        this._dateSelectListener = function (value) { return _this._onDateSelected(value); };
        this._dropdownCloseListener = function () { return _this._onDropdownClose(); };
        this._activeChangeListener = function (id) { return _this._onActiveDescendantChanged(id); };
        this._todayListener = function () { return _this._onToday(); };
        this._clearListener = function () { return _this._onClear(); };
    }
    DateRangePickerFoundation.prototype.initialize = function () {
        this._adapter.initialize();
        this._adapter.initializeAccessibility();
        if (!this._adapter.hasInputElement()) {
            throw new Error('Unable to locate <input> element to attach to.');
        }
        if (!this._from) {
            this._from = this._coerceDateValue(this._adapter.getFromInputValue());
        }
        if (!this._to) {
            this._to = this._coerceDateValue(this._adapter.getToInputValue());
        }
        this._adapter.tryCreateToggle();
        if (!this._masked) {
            this._adapter.addFromInputListener('input', this._fromInputListener);
            this._adapter.addToInputListener('input', this._toInputListener);
        }
        this._adapter.addFromInputListener('keydown', this._fromInputKeydownListener);
        this._adapter.addToInputListener('keydown', this._toInputKeydownListener);
        this._adapter.addFromInputListener('focus', this._fromInputFocusListener);
        this._adapter.addToInputListener('focus', this._toInputFocusListener);
        this._adapter.addFromInputListener('blur', this._fromInputBlurListener);
        this._adapter.addToInputListener('blur', this._toInputBlurListener);
        this._adapter.addToggleListener('mousedown', this._toggleMousedownListener);
        this._setFormattedFromInputValue();
        this._setFormattedToInputValue();
        this._applyDisabled();
        this._applyMask();
        this._adapter.setFromInputValueChangedListener(this, this._fromInputValueChangedListener);
        this._adapter.setToInputValueChangedListener(this, this._toInputValueChangedListener);
        this._isInitialized = true;
    };
    DateRangePickerFoundation.prototype.disconnect = function () {
        this._isInitialized = false;
        this._adapter.addFromInputListener('input', this._fromInputListener);
        this._adapter.removeToInputListener('input', this._toInputListener);
        this._adapter.removeFromInputListener('keydown', this._fromInputKeydownListener);
        this._adapter.removeToInputListener('keydown', this._toInputKeydownListener);
        this._adapter.removeFromInputListener('focus', this._fromInputFocusListener);
        this._adapter.removeToInputListener('focus', this._toInputFocusListener);
        this._adapter.removeFromInputListener('blur', this._fromInputBlurListener);
        this._adapter.removeToInputListener('blur', this._toInputBlurListener);
        this._adapter.removeToggleListener('mousedown', this._toggleMousedownListener);
        if (this._masked) {
            this._adapter.destroyMask();
        }
        this._closeCalendar();
        this._adapter.destroy();
    };
    DateRangePickerFoundation.prototype._applyDisabled = function () {
        this._adapter.setDisabled(this._disabled);
    };
    DateRangePickerFoundation.prototype._applyMask = function () {
        var _this = this;
        if (this._masked) {
            var options = {
                showMaskFormat: this._showMaskFormat,
                pattern: this._maskFormat
            };
            if (this._prepareMaskCallback) {
                options.prepareCallback = function (value, masked, flags, maskInstance) {
                    return _this._prepareMaskCallback.call(null, value, masked, flags, maskInstance);
                };
            }
            var fromOptions = __assign(__assign({}, options), { onChange: function (value) { return _this._handleFromInput(value); } });
            var toOptions = __assign(__assign({}, options), { onChange: function (value) { return _this._handleToInput(value); } });
            this._adapter.initializeMask(fromOptions, toOptions);
        }
        else {
            this._adapter.destroyMask();
            this._formatFromInputValue();
        }
    };
    DateRangePickerFoundation.prototype._isSameDate = function (first, second) {
        if (!first && !second) {
            return true;
        }
        else if (!first || !second) {
            return false;
        }
        else if (!isValidDate(first) || !isValidDate(second)) {
            return false;
        }
        return first.setHours(0, 0, 0, 0) === second.setHours(0, 0, 0, 0);
    };
    DateRangePickerFoundation.prototype._onFromInput = function (evt) {
        this._handleFromInput(this._adapter.getFromInputValue());
    };
    DateRangePickerFoundation.prototype._onToInput = function (evt) {
        this._handleToInput(this._adapter.getToInputValue());
    };
    DateRangePickerFoundation.prototype._handleFromInput = function (value) {
        var sanitizedValue = this._getSanitizedDateString(value);
        var date = this._coerceDateValue(sanitizedValue);
        if (this._masked) {
            this._adapter.emitFromInputEvent(DATE_RANGE_PICKER_CONSTANTS.events.INPUT, sanitizedValue);
        }
        if (!this._isSameDate(date, this._from) && this._isDateValueAcceptable(date)) {
            this._emitChangeEvent(new DateRange({ from: date || undefined, to: this._to || undefined }));
        }
    };
    DateRangePickerFoundation.prototype._handleToInput = function (value) {
        var sanitizedValue = this._getSanitizedDateString(value);
        var date = this._coerceDateValue(sanitizedValue);
        if (this._masked) {
            this._adapter.emitToInputEvent(DATE_RANGE_PICKER_CONSTANTS.events.INPUT, sanitizedValue);
        }
        if (!this._isSameDate(date, this._to) && this._isDateValueAcceptable(date)) {
            this._emitChangeEvent(new DateRange({ from: this._from || undefined, to: date || undefined }));
        }
    };
    DateRangePickerFoundation.prototype._getSanitizedDateString = function (value) {
        return value.replace(/_|[a-z]|[A-Z]/g, '').replace(/\/$|\/\/$/, '');
    };
    DateRangePickerFoundation.prototype._onInputKeydown = function (evt) {
        var _this = this;
        if (evt.shiftKey) {
            switch (evt.code) {
                case 'Delete':
                case 'Backspace':
                    evt.preventDefault();
                    this._onClear();
                    if (this._open) {
                        this._closeCalendar(true);
                    }
                    return;
                case 'ArrowLeft':
                case 'ArrowRight':
                case 'KeyM':
                case 'KeyY':
                case 'KeyD':
                    if (this._open) {
                        evt.preventDefault();
                        this._adapter.propagateCalendarKey(evt.code, true);
                        return;
                    }
                    break;
            }
        }
        switch (evt.code) {
            case 'Esc':
            case 'Escape':
                evt.preventDefault();
                if (this._open) {
                    this._closeCalendar(true);
                }
                break;
            case 'Down':
            case 'ArrowDown':
                evt.preventDefault();
                if (!this._open) {
                    this._adapter.selectFromInputText();
                    // Requestion animation frame due to IE issue. Would blur off
                    window.requestAnimationFrame(function () {
                        _this._openCalendar(true);
                    });
                }
                else {
                    this._adapter.propagateCalendarKey(evt.code);
                }
                break;
            case 'Up':
            case 'ArrowUp':
                if (this._open) {
                    evt.preventDefault();
                    this._adapter.propagateCalendarKey(evt.code);
                }
                break;
            case 'Left':
            case 'Right':
            case 'ArrowLeft':
            case 'ArrowRight':
            case 'Enter':
            case 'Home':
            case 'End':
                if (this._open) {
                    evt.preventDefault();
                    // If we have an input mask applied, we want to make sure it doesn't receive the key
                    // events for left and right keys
                    var isLeftRight = ['ArrowLeft', 'Left', 'ArrowRight', 'Right'].includes(evt.code);
                    if (this._masked && isLeftRight) {
                        evt.stopImmediatePropagation();
                    }
                    this._adapter.propagateCalendarKey(evt.code);
                }
                break;
            case 'KeyN':
            case 'KeyT':
                evt.preventDefault();
                this._onToday();
                if (this._open) {
                    this._closeCalendar(true);
                }
                break;
        }
    };
    DateRangePickerFoundation.prototype._onFromInputFocus = function () {
        this._adapter.selectFromInputText();
    };
    DateRangePickerFoundation.prototype._onToInputFocus = function () {
        this._adapter.selectToInputText();
    };
    DateRangePickerFoundation.prototype._onFromInputBlur = function (evt) {
        this._formatFromInputValue();
        if (this._open) {
            this._closeCalendar(true);
        }
    };
    DateRangePickerFoundation.prototype._onToInputBlur = function (evt) {
        this._formatToInputValue();
        if (this._open) {
            this._closeCalendar(true);
        }
    };
    DateRangePickerFoundation.prototype._onFromInputValueChanged = function (value) {
        if (this._masked) {
            return;
        }
        var sanitizedValue = this._getSanitizedDateString(value);
        var date = this._coerceDateValue(sanitizedValue);
        if (!this._isSameDate(date, this._from)) {
            this.from = date;
            this._emitChangeEvent(new DateRange({ from: date || undefined, to: this._to || undefined }));
        }
    };
    DateRangePickerFoundation.prototype._onToInputValueChanged = function (value) {
        if (this._masked) {
            return;
        }
        var sanitizedValue = this._getSanitizedDateString(value);
        var date = this._coerceDateValue(sanitizedValue);
        if (!this._isSameDate(date, this._to)) {
            this.to = date;
            this._emitChangeEvent(new DateRange({ from: this._from || undefined, to: date || undefined }));
        }
    };
    DateRangePickerFoundation.prototype._onToggleMousedown = function (evt) {
        var _this = this;
        if (this._disabled) {
            return;
        }
        evt.stopPropagation();
        if (this._adapter.isInputDisabled()) {
            return;
        }
        evt.preventDefault();
        if (this._open) {
            this._closeCalendar(true);
        }
        else {
            if (!Platform.isMobile) {
                this._adapter.tryFocusInput();
            }
            else {
                this._adapter.tryBlurInput();
            }
            // Requesting frame so that we know the focus has been set
            window.requestAnimationFrame(function () { return _this._openCalendar(true); });
        }
    };
    DateRangePickerFoundation.prototype._emitChangeEvent = function (value, force) {
        if (force === void 0) { force = false; }
        var typedStartValue = this._getTypedValue((value && value.from) || null);
        var typedEndValue = this._getTypedValue((value && value.to) || null);
        var detail = new DatePickerRange({ from: typedStartValue, to: typedEndValue });
        var wasCancelled = !this._adapter.emitHostEvent(DATE_RANGE_PICKER_CONSTANTS.events.CHANGE, detail, true, !force);
        if (!wasCancelled) {
            this._updateFromValue(this._coerceDateValue((value && value.from) || null));
            this._updateToValue(this._coerceDateValue((value && value.to) || null));
            return true;
        }
        return false;
    };
    DateRangePickerFoundation.prototype._onDropdownClose = function () {
        this._closeCalendar(true);
    };
    DateRangePickerFoundation.prototype._onDateSelected = function (event) {
        var value = event.range;
        if (event.type === CalendarDateSelectType.date) {
            if (value instanceof DateRange && value.from instanceof Date && value.to instanceof Date) {
                this._closeCalendar(true);
            }
        }
        if (!this._emitChangeEvent(value)) {
            return;
        }
        var formattedFromValue = this._formatDate((value && value.from) || null);
        var formattedToValue = this._formatDate((value && value.to) || null);
        this._adapter.setFromInputValue(formattedFromValue, this._notifyInputValueChanges);
        this._adapter.setToInputValue(formattedToValue, this._notifyInputValueChanges);
        this._from = (value && value.from) || null;
        this._to = (value && value.to) || null;
        if (!Platform.isMobile) {
            if (formattedToValue) {
                this._adapter.selectToInputText();
            }
            else {
                this._adapter.selectFromInputText();
            }
        }
    };
    DateRangePickerFoundation.prototype._onActiveDescendantChanged = function (id) {
        this._adapter.setActiveDescendant(id);
    };
    DateRangePickerFoundation.prototype._onToday = function () {
        var today = new Date();
        var range = new DateRange({ from: today });
        this.value = range;
        this._onDateSelected({ range: range, isSelected: true, type: CalendarDateSelectType.date });
        this._adapter.setActiveDate(today);
    };
    DateRangePickerFoundation.prototype._onClear = function () {
        this._onDateSelected({ range: new DateRange(), isSelected: false, type: CalendarDateSelectType.date });
        this._closeCalendar(true);
    };
    DateRangePickerFoundation.prototype._openCalendar = function (emitOpenEvent) {
        if (emitOpenEvent === void 0) { emitOpenEvent = false; }
        this._formatFromInputValue();
        this._formatToInputValue();
        this._open = true;
        this._adapter.setHostAttribute(DATE_RANGE_PICKER_CONSTANTS.attributes.OPEN, '');
        var calendarConfig = {
            value: new DateRange({ from: this._from || undefined, to: this._to || undefined }),
            min: this._min,
            max: this._max,
            blockedDates: this._blockedDates,
            popupClasses: this._popupClasses,
            dialogWidthThreshold: this._dialogWidthThreshold,
            closeCallback: this._dropdownCloseListener,
            selectCallback: this._dateSelectListener,
            activeChangeCallback: this._activeChangeListener,
            showToday: this._showToday,
            showClear: this._showClear,
            todayCallback: this._todayListener,
            clearCallback: this._clearListener,
            disableDayCallback: this._disableDayCallback,
            disabledDaysOfWeek: this._disabledDaysOfWeek,
            startView: this._startView
        };
        this._adapter.attachCalendar(calendarConfig);
        if (emitOpenEvent) {
            this._adapter.emitHostEvent(DATE_RANGE_PICKER_CONSTANTS.events.OPEN, true, false);
        }
    };
    DateRangePickerFoundation.prototype._closeCalendar = function (emitCloseEvent) {
        if (emitCloseEvent === void 0) { emitCloseEvent = false; }
        this._open = false;
        this._adapter.removeHostAttribute(DATE_RANGE_PICKER_CONSTANTS.attributes.OPEN);
        this._adapter.detachCalendar();
        if (emitCloseEvent) {
            this._adapter.emitHostEvent(DATE_RANGE_PICKER_CONSTANTS.events.CLOSE, true, false);
        }
    };
    DateRangePickerFoundation.prototype._formatFromInputValue = function () {
        var inputValue = this._adapter.getFromInputValue();
        if (inputValue) {
            var parsedDate = this._parseDateString(inputValue);
            if (isValidDate(parsedDate) && this._isDateValueAcceptable(parsedDate)) {
                var formattedDate = this._formatDate(parsedDate);
                if (formattedDate) {
                    this._adapter.setFromInputValue(formattedDate, this._notifyInputValueChanges);
                }
            }
            else {
                if (!this._allowInvalidDate) {
                    this._adapter.setFromInputValue('', this._notifyInputValueChanges);
                }
            }
        }
    };
    DateRangePickerFoundation.prototype._formatToInputValue = function () {
        var inputValue = this._adapter.getToInputValue();
        if (inputValue) {
            var parsedDate = this._parseDateString(inputValue);
            if (isValidDate(parsedDate) && this._isDateValueAcceptable(parsedDate)) {
                var formattedDate = this._formatDate(parsedDate);
                if (formattedDate) {
                    this._adapter.setToInputValue(formattedDate, this._notifyInputValueChanges);
                }
            }
            else {
                if (!this._allowInvalidDate) {
                    this._adapter.setToInputValue('', this._notifyInputValueChanges);
                }
            }
        }
    };
    DateRangePickerFoundation.prototype._setFormattedFromInputValue = function () {
        var formattedDate = this._formatDate(this._from);
        if (formattedDate) {
            this._adapter.setFromInputValue(formattedDate, this._notifyInputValueChanges);
        }
        else {
            if (!this._allowInvalidDate) {
                this._adapter.setFromInputValue('', this._notifyInputValueChanges);
            }
        }
    };
    DateRangePickerFoundation.prototype._setFormattedToInputValue = function () {
        var formattedDate = this._formatDate(this._to);
        if (formattedDate) {
            this._adapter.setToInputValue(formattedDate, this._notifyInputValueChanges);
        }
        else {
            if (!this._allowInvalidDate) {
                this._adapter.setToInputValue('', this._notifyInputValueChanges);
            }
        }
    };
    DateRangePickerFoundation.prototype._formatDate = function (date) {
        if (!isValidDate(date)) {
            return '';
        }
        return typeof this._formatCallback === 'function' ? this._formatCallback(date) : formatDate(date);
    };
    DateRangePickerFoundation.prototype._parseDateString = function (value) {
        value = value.replace(/\s+/g, '');
        if (!value || !value.length) {
            return null;
        }
        var parsedDate = typeof this._parseCallback === 'function' ? this._parseCallback(value) : parseDateString(value);
        return isValidDate(parsedDate) ? parsedDate : null;
    };
    DateRangePickerFoundation.prototype._coerceDateValue = function (value) {
        if (typeof value === 'string') {
            return this._parseDateString(value);
        }
        return value;
    };
    DateRangePickerFoundation.prototype._getTypedValue = function (value) {
        switch (this._valueMode) {
            case 'object':
                return value;
            case 'string':
                return this._formatDate(value);
            case 'iso-string':
                if (value) {
                    // Remove the time portion of the date
                    value.setHours(0, 0, 0, 0);
                }
                return value ? value.toISOString() : null;
            default:
                return value;
        }
    };
    DateRangePickerFoundation.prototype._isDateValueAcceptable = function (value) {
        if (!value) {
            return true;
        }
        var passesMinDate = this._min ? this._min.getTime() <= value.getTime() : true;
        var passesMaxDate = this._max ? this._max.getTime() >= value.getTime() : true;
        var blockedDates = Array.isArray(this._blockedDates) ? this._blockedDates : this._blockedDates ? [this._blockedDates] : [];
        var isNotBlocked = !blockedDates.some(function (bd) { return bd.getTime() === value.getTime(); });
        var dayNotDisabled = !(this._disabledDaysOfWeek && this._disabledDaysOfWeek.includes(value.getDay()));
        var dateNotDisabledThroughCallback = typeof this._disableDayCallback !== 'function' || !this._disableDayCallback(value);
        return passesMinDate && passesMaxDate && isNotBlocked && dayNotDisabled && dateNotDisabledThroughCallback;
    };
    DateRangePickerFoundation.prototype._updateToValue = function (value) {
        if (!value || this._isDateValueAcceptable(value)) {
            this._to = value || null;
        }
    };
    DateRangePickerFoundation.prototype._updateFromValue = function (value) {
        if (!value || this._isDateValueAcceptable(value)) {
            this._from = value || null;
        }
    };
    Object.defineProperty(DateRangePickerFoundation.prototype, "from", {
        get: function () {
            var date = this._getTypedValue(this._from);
            if (!date) {
                return null;
            }
            if (isDate(date)) {
                return new Date(date.getTime());
            }
            return date;
        },
        set: function (value) {
            if (this._from !== value) {
                this._updateFromValue(this._coerceDateValue(value));
                if (this._isInitialized) {
                    this._setFormattedFromInputValue();
                    if (this._open) {
                        this._adapter.setCalendarValue(new DateRange({ from: this._coerceDateValue(value) || undefined, to: this._coerceDateValue(this._to) || undefined }));
                    }
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DateRangePickerFoundation.prototype, "to", {
        get: function () {
            var date = this._getTypedValue(this._to);
            if (!date) {
                return null;
            }
            if (isDate(date)) {
                return new Date(date.getTime());
            }
            return date;
        },
        set: function (value) {
            if (this._to !== value) {
                this._updateToValue(this._coerceDateValue(value));
                if (this._isInitialized) {
                    this._setFormattedToInputValue();
                    if (this._open) {
                        this._adapter.setCalendarValue(new DateRange({ to: this._coerceDateValue(value) || undefined, from: this._coerceDateValue(this._from) || undefined }));
                    }
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DateRangePickerFoundation.prototype, "min", {
        get: function () {
            if (!this._min) {
                return null;
            }
            return new Date(this._min.getTime());
        },
        set: function (value) {
            if (this._min !== value) {
                var date = this._coerceDateValue(value);
                this._min = !!date ? new Date(date.getTime()) : null;
                if (this._from && !this._isDateValueAcceptable(this._from)) {
                    this._emitChangeEvent(new DateRange({ to: this._to || undefined }), true);
                    this._setFormattedFromInputValue();
                }
                if (this._to && !this._isDateValueAcceptable(this._to)) {
                    this._emitChangeEvent(new DateRange({ from: this._from || undefined }), true);
                    this._setFormattedToInputValue();
                }
                if (this._isInitialized) {
                    this._adapter.setCalendarMinDate(this._min);
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DateRangePickerFoundation.prototype, "max", {
        get: function () {
            if (!this._max) {
                return null;
            }
            return new Date(this._max.getTime());
        },
        set: function (value) {
            if (this._max !== value) {
                this._max = this._coerceDateValue(value);
                if (this._from && !this._isDateValueAcceptable(this._from)) {
                    this._emitChangeEvent(new DateRange({ to: this._to || undefined }), true);
                    this._setFormattedFromInputValue();
                }
                if (this._to && !this._isDateValueAcceptable(this._to)) {
                    this._emitChangeEvent(new DateRange({ from: this._from || undefined }), true);
                    this._setFormattedToInputValue();
                }
                if (this._isInitialized) {
                    this._adapter.setCalendarMaxDate(this._max);
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DateRangePickerFoundation.prototype, "blockedDates", {
        get: function () {
            if (!this._blockedDates) {
                return null;
            }
            if (Array.isArray(this._blockedDates)) {
                return this._blockedDates.map(function (d) { return new Date(d.getTime()); });
            }
            return new Date(this._blockedDates.getTime());
        },
        set: function (value) {
            if (!value) {
                this._blockedDates = null;
            }
            else if (Array.isArray(value)) {
                this._blockedDates = value.map(function (d) { return new Date(d.getTime()); });
            }
            else {
                this._blockedDates = new Date(value.getTime());
            }
            if (this._isInitialized && this._open) {
                this._adapter.setCalendarBlockedDates(this._blockedDates);
            }
            if (this._from && !this._isDateValueAcceptable(this._from)) {
                this._emitChangeEvent(new DateRange({ to: this._to || undefined }), true);
                this._setFormattedFromInputValue();
            }
            if (this._to && !this._isDateValueAcceptable(this._to)) {
                this._emitChangeEvent(new DateRange({ from: this._from || undefined }), true);
                this._setFormattedToInputValue();
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DateRangePickerFoundation.prototype, "open", {
        get: function () {
            return this._open;
        },
        set: function (value) {
            if (this._open !== value) {
                if (this._isInitialized) {
                    this._open = value;
                    if (this._open) {
                        this._openCalendar();
                    }
                    else {
                        this._closeCalendar();
                    }
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DateRangePickerFoundation.prototype, "disabled", {
        get: function () {
            return this._disabled;
        },
        set: function (value) {
            if (this._disabled !== value) {
                this._disabled = value;
                if (this._isInitialized) {
                    this._applyDisabled();
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DateRangePickerFoundation.prototype, "popupClasses", {
        get: function () {
            if (Array.isArray(this._popupClasses)) {
                return __spreadArrays(this._popupClasses);
            }
            return this._popupClasses;
        },
        set: function (value) {
            if (Array.isArray(value)) {
                this._popupClasses = __spreadArrays(value);
            }
            else {
                this._popupClasses = value;
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DateRangePickerFoundation.prototype, "masked", {
        get: function () {
            return this._masked;
        },
        set: function (value) {
            if (this._masked !== value) {
                this._masked = value;
                if (this._isInitialized) {
                    if (this._masked) {
                        this._adapter.removeFromInputListener('input', this._fromInputListener);
                        this._adapter.removeToInputListener('input', this._toInputListener);
                        this._applyMask();
                    }
                    else {
                        this._adapter.addFromInputListener('input', this._fromInputListener);
                        this._adapter.addToInputListener('input', this._toInputListener);
                        this._adapter.destroyMask();
                        this._formatFromInputValue();
                    }
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DateRangePickerFoundation.prototype, "maskFormat", {
        get: function () {
            return this._maskFormat;
        },
        set: function (value) {
            if (this._maskFormat !== value) {
                this._maskFormat = value;
                if (this._isInitialized) {
                    this._applyMask();
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DateRangePickerFoundation.prototype, "useCharMask", {
        get: function () {
            return this._useCharMask;
        },
        set: function (value) {
            if (this._useCharMask !== value) {
                this._useCharMask = value;
                if (this._isInitialized) {
                    this._applyMask();
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DateRangePickerFoundation.prototype, "showMaskFormat", {
        get: function () {
            return this._showMaskFormat;
        },
        set: function (value) {
            if (this._showMaskFormat !== value) {
                this._showMaskFormat = value;
                if (this._isInitialized) {
                    this._applyMask();
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DateRangePickerFoundation.prototype, "valueMode", {
        get: function () {
            return this._valueMode;
        },
        set: function (value) {
            if (this._valueMode !== value) {
                var originalValueMode = this._valueMode;
                var isValidMode = ['object', 'string', 'iso-string'].includes(value);
                this._valueMode = isValidMode ? value : 'object';
                if (originalValueMode !== this._valueMode) {
                    this._emitChangeEvent(new DateRange({ from: this._from || undefined, to: this._to || undefined }));
                    this._adapter.setHostAttribute(DATE_RANGE_PICKER_CONSTANTS.attributes.VALUE_MODE, this._valueMode);
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DateRangePickerFoundation.prototype, "dialogWidthThreshold", {
        set: function (value) {
            this._dialogWidthThreshold = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DateRangePickerFoundation.prototype, "notifyInputValueChanges", {
        get: function () {
            return this._notifyInputValueChanges;
        },
        set: function (value) {
            this._notifyInputValueChanges = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DateRangePickerFoundation.prototype, "allowInvalidDate", {
        get: function () {
            return this._allowInvalidDate;
        },
        set: function (value) {
            if (this._allowInvalidDate !== value) {
                this._allowInvalidDate = value;
                if (this._allowInvalidDate) {
                    this._adapter.setHostAttribute(DATE_RANGE_PICKER_CONSTANTS.attributes.ALLOW_INVALID_DATE);
                }
                else {
                    this._adapter.removeHostAttribute(DATE_RANGE_PICKER_CONSTANTS.attributes.ALLOW_INVALID_DATE);
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DateRangePickerFoundation.prototype, "disabledDaysOfWeek", {
        get: function () {
            return this._disabledDaysOfWeek;
        },
        set: function (value) {
            if (value !== this._disabledDaysOfWeek) {
                this._disabledDaysOfWeek = value;
                this.applyDisabledDaysOfWeek();
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DateRangePickerFoundation.prototype, "disableDayCallback", {
        set: function (value) {
            if (value !== this._disableDayCallback) {
                this._disableDayCallback = value;
                this._applyDisableDayCallback();
            }
        },
        enumerable: true,
        configurable: true
    });
    DateRangePickerFoundation.prototype._applyDisableDayCallback = function () {
        this._adapter.setDisableDayCallback(this._disableDayCallback);
    };
    DateRangePickerFoundation.prototype.applyDisabledDaysOfWeek = function () {
        if (this._isInitialized && this._open) {
            this._adapter.setCalendarDisabledDaysOfWeek(this._disabledDaysOfWeek);
        }
        if (this._from && !this._isDateValueAcceptable(this._from)) {
            this._emitChangeEvent(null, true);
            this._setFormattedFromInputValue();
        }
        if (this._to && !this._isDateValueAcceptable(this._to)) {
            this._emitChangeEvent(null, true);
            this._setFormattedToInputValue();
        }
    };
    Object.defineProperty(DateRangePickerFoundation.prototype, "parseCallback", {
        get: function () {
            return this._parseCallback;
        },
        set: function (cb) {
            this._parseCallback = cb;
            if (this._isInitialized) {
                this._applyMask();
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DateRangePickerFoundation.prototype, "formatCallback", {
        get: function () {
            return this._formatCallback;
        },
        set: function (cb) {
            this._formatCallback = cb;
            if (this._isInitialized) {
                this._applyMask();
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DateRangePickerFoundation.prototype, "prepareMaskCallback", {
        get: function () {
            return this._prepareMaskCallback;
        },
        set: function (cb) {
            this._prepareMaskCallback = cb;
            if (this._isInitialized) {
                this._applyMask();
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DateRangePickerFoundation.prototype, "value", {
        get: function () {
            return {
                from: this.from,
                to: this.to
            };
        },
        set: function (value) {
            if (!value) {
                return;
            }
            if (value.from === undefined) {
                value.from = null;
            }
            if (value.to === undefined) {
                value.to = null;
            }
            this.from = value.from;
            this.to = value.to;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DateRangePickerFoundation.prototype, "showToday", {
        get: function () {
            return this._showToday;
        },
        set: function (value) {
            this._showToday = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DateRangePickerFoundation.prototype, "showClear", {
        get: function () {
            return this._showClear;
        },
        set: function (value) {
            this._showClear = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DateRangePickerFoundation.prototype, "startView", {
        get: function () {
            return this._startView;
        },
        set: function (value) {
            if (value !== this._startView) {
                this._startView = value;
            }
        },
        enumerable: true,
        configurable: true
    });
    return DateRangePickerFoundation;
}());
export { DateRangePickerFoundation };
